/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListCustomDetectionRulesRequest,
  ListCustomDetectionRulesSecurity,
} from "../models/operations/listcustomdetectionrules.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskListCustomDetectionRulesQuery,
  prefetchRiskListCustomDetectionRules,
  queryKeyRiskListCustomDetectionRules,
  RiskListCustomDetectionRulesQueryData,
} from "./riskListCustomDetectionRules.core.js";
export {
  buildRiskListCustomDetectionRulesQuery,
  prefetchRiskListCustomDetectionRules,
  queryKeyRiskListCustomDetectionRules,
  type RiskListCustomDetectionRulesQueryData,
};

export type RiskListCustomDetectionRulesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listCustomDetectionRules risk
 *
 * @remarks
 * List custom detection rules for the current project.
 */
export function useRiskListCustomDetectionRules(
  request?: ListCustomDetectionRulesRequest | undefined,
  security?: ListCustomDetectionRulesSecurity | undefined,
  options?: QueryHookOptions<
    RiskListCustomDetectionRulesQueryData,
    RiskListCustomDetectionRulesQueryError
  >,
): UseQueryResult<
  RiskListCustomDetectionRulesQueryData,
  RiskListCustomDetectionRulesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskListCustomDetectionRulesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listCustomDetectionRules risk
 *
 * @remarks
 * List custom detection rules for the current project.
 */
export function useRiskListCustomDetectionRulesSuspense(
  request?: ListCustomDetectionRulesRequest | undefined,
  security?: ListCustomDetectionRulesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskListCustomDetectionRulesQueryData,
    RiskListCustomDetectionRulesQueryError
  >,
): UseSuspenseQueryResult<
  RiskListCustomDetectionRulesQueryData,
  RiskListCustomDetectionRulesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskListCustomDetectionRulesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskListCustomDetectionRulesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskListCustomDetectionRulesQueryData,
): RiskListCustomDetectionRulesQueryData | undefined {
  const key = queryKeyRiskListCustomDetectionRules(...queryKeyBase);

  return client.setQueryData<RiskListCustomDetectionRulesQueryData>(key, data);
}

export function invalidateRiskListCustomDetectionRules(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "customRules", "list", ...queryKeyBase],
  });
}

export function invalidateAllRiskListCustomDetectionRules(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "customRules", "list"],
  });
}
